/**
 * BookinglayerService
 *
 * Real API service for all Bookinglayer integration endpoints.
 * Maps to the Django Bookinglayer endpoints; see shaka-api/apps/integrations/services/BOOKINGLAYER.md for integration-specific reference.
 * Base prefix: /api/integrations/bookinglayer/
 *
 * Conventions:
 * - Request bodies are written in camelCase — apiClient converts them to
 *   snake_case at the boundary. Never hand-build snake_case bodies here.
 * - Forensic subtrees (webhook payloads, BL response bodies) and maps whose
 *   keys are data (gateway slugs) opt out of the case transforms via
 *   `preserveKeys` so the operator sees exactly what went over the wire.
 */

import { apiClient } from '@/shared/services/api';
import type {
  BlCatalogDecision,
  BlCatalogDecisionResult,
  BlCatalogImportResult,
  BlBookingEntitlements,
  BlPlaceResult,
  BlCatalogResponse,
  BlPaymentGateway,
  ValidateTokenResponse,
  PreviewBookingsResponse,
  ConnectPayload,
  ConnectionStatusResponse,
  BookinglayerSyncJob,
  BookinglayerSyncConfig,
  BookinglayerAlert,
  BookinglayerSeasonInfoResponse,
  WebhookLogsResponse,
  WebhookLogDetailResponse,
  OutboundEventsResponse,
  OutboundEventRetryResponse,
  OutboundEventStatus,
  OutboundEventResourceType,
} from '../types';

/**
 * Date range filter that maps to the backend's
 *   starts_at_from / starts_at_to (or updated_at_*, created_at_*) pair.
 * Pass either (or both) bounds as ISO `YYYY-MM-DD`.
 */
export interface SyncScopeRange {
  startsAtFrom?: string | null;
  startsAtTo?: string | null;
  updatedAtFrom?: string | null;
  updatedAtTo?: string | null;
  createdAtFrom?: string | null;
  createdAtTo?: string | null;
}

/** Config-map keys whose contents are data, not field names. */
const CONFIG_MAP_KEYS = [
  'gatewayMethodMap',
  'outboundGatewayMap',
  'outboundChargeAmendmentTitles',
  'outboundChargeTaxRates',
] as const;

const BASE = '/api/integrations/bookinglayer';

export const BookinglayerService = {
  // === Setup Wizard ===

  /** POST validate-token/ — Test API key, returns available BL locations */
  async validateToken(apiKey: string): Promise<ValidateTokenResponse> {
    const { data } = await apiClient.post<ValidateTokenResponse>(
      `${BASE}/validate-token/`,
      { apiKey },
    );
    return data;
  },

  /** POST preview-bookings/ — The 20 most recently created BL bookings, for the wizard's confirmation step */
  async previewBookings(apiKey: string, blLocationId: number): Promise<PreviewBookingsResponse> {
    const { data } = await apiClient.post<PreviewBookingsResponse>(
      `${BASE}/preview-bookings/`,
      { apiKey, blLocationId },
    );
    return data;
  },

  /** POST connect/ — Create connection + trigger initial sync */
  async connect(payload: ConnectPayload): Promise<{ connectionId: string; jobId: string }> {
    const body: Record<string, unknown> = { ...payload };
    // Scope dates are only forwarded when set so the backend can fall
    // back to "everything BL has" as the unbounded default.
    if (!payload.initialSyncStartsAtFrom) delete body.initialSyncStartsAtFrom;
    if (!payload.initialSyncStartsAtTo) delete body.initialSyncStartsAtTo;
    const { data } = await apiClient.post<{ connectionId: string; jobId: string }>(
      `${BASE}/connect/`, body,
    );
    return { connectionId: data.connectionId, jobId: data.jobId };
  },

  /**
   * GET locations/{location_id}/season-info/ — Drives the wizard's scope
   * step. Returns whether the location has any active/planned seasons,
   * the suggested ``from`` date (earliest season start), and the season
   * list for an optional picker.
   */
  async getSeasonInfo(locationId: string): Promise<BookinglayerSeasonInfoResponse> {
    const { data } = await apiClient.get<BookinglayerSeasonInfoResponse>(
      `${BASE}/locations/${locationId}/season-info/`,
    );
    return data;
  },

  // === Operations ===

  /** GET status/ — Connection state + sync config */
  async getStatus(): Promise<ConnectionStatusResponse> {
    const { data } = await apiClient.get<ConnectionStatusResponse>(`${BASE}/status/`, {
      preserveKeys: CONFIG_MAP_KEYS,
    });
    return data;
  },

  /**
   * POST sync/ — Trigger manual sync.
   *
   * Backend default WITHOUT a scope: the sync is constrained to the
   * location's ACTIVE SEASON window by check-in date (± the season
   * margin days) — see BookinglayerSyncService.sync_connection. Only
   * when the location has no resolvable season does it fall back to
   * everything BL has. It is NOT a delta since the last sync (that
   * mode belongs to the daily cron, which filters by updated_at).
   *
   * Pass a `scope` range to override the default window explicitly.
   */
  async triggerSync(scope?: SyncScopeRange): Promise<{ jobId: string }> {
    const body: Record<string, unknown> = {};
    if (scope?.startsAtFrom) body.startsAtFrom = scope.startsAtFrom;
    if (scope?.startsAtTo) body.startsAtTo = scope.startsAtTo;
    if (scope?.updatedAtFrom) body.updatedAtFrom = scope.updatedAtFrom;
    if (scope?.updatedAtTo) body.updatedAtTo = scope.updatedAtTo;
    if (scope?.createdAtFrom) body.createdAtFrom = scope.createdAtFrom;
    if (scope?.createdAtTo) body.createdAtTo = scope.createdAtTo;
    const { data } = await apiClient.post<{ jobId: string }>(`${BASE}/sync/`, body);
    return { jobId: data.jobId };
  },

  /** GET sync/jobs/ — Last 20 sync jobs */
  async getSyncJobs(): Promise<BookinglayerSyncJob[]> {
    const { data } = await apiClient.get<BookinglayerSyncJob[]>(`${BASE}/sync/jobs/`);
    return data;
  },

  /** GET sync-config/ — Current sync direction config */
  async getSyncConfig(): Promise<BookinglayerSyncConfig> {
    const { data } = await apiClient.get<BookinglayerSyncConfig>(`${BASE}/sync-config/`, {
      preserveKeys: CONFIG_MAP_KEYS,
    });
    return data;
  },

  /** GET payment-gateways/ — BL gateways for the connection (mapping editor). */
  async getPaymentGateways(): Promise<BlPaymentGateway[]> {
    const { data } = await apiClient.get<{ gateways: BlPaymentGateway[] }>(
      `${BASE}/payment-gateways/`,
    );
    return data.gateways ?? [];
  },

  /**
   * GET catalog/ — everything the location sells in BL, and what each product
   * currently maps to here. Read-only against BL. Accommodation is excluded —
   * the room sync owns it.
   */
  async getCatalog(): Promise<BlCatalogResponse> {
    const { data } = await apiClient.get<BlCatalogResponse>(`${BASE}/catalog/`);
    return data;
  },

  /**
   * PATCH catalog/{externalProductId}/ — record what a BL product becomes here.
   * Passing `targetId` links to a row that already exists (a service created in
   * deck, matched to its BL product afterwards).
   */
  async setCatalogDecision(
    externalProductId: string,
    decision: BlCatalogDecision,
  ): Promise<BlCatalogDecisionResult> {
    const { data } = await apiClient.patch<BlCatalogDecisionResult>(
      `${BASE}/catalog/${externalProductId}/`,
      decision,
    );
    return data;
  },

  /**
   * POST catalog/import/ — create the catalog rows the decisions call for.
   * Idempotent: rows already linked are skipped, so re-running creates nothing.
   */
  async importCatalog(): Promise<BlCatalogImportResult> {
    const { data } = await apiClient.post<BlCatalogImportResult>(`${BASE}/catalog/import/`);
    return data;
  },

  /**
   * GET bookings/{bookingId}/entitlements/ — the "Plan the stay" queue: per
   * guest, the activity quota they bought in BL and what's placed so far.
   */
  async getBookingEntitlements(bookingId: string): Promise<BlBookingEntitlements> {
    const { data } = await apiClient.get<BlBookingEntitlements>(
      `${BASE}/bookings/${bookingId}/entitlements/`,
    );
    return data;
  },

  /**
   * POST entitlements/place/ — place activity sessions against a guest's quota.
   * Each activity id becomes one enrollment (source=bookinglayer, no charge);
   * the backend rejects placing more than the remaining quota.
   */
  async placeEntitlement(grantId: string, activityIds: string[]): Promise<BlPlaceResult> {
    const { data } = await apiClient.post<BlPlaceResult>(
      `${BASE}/entitlements/place/`,
      { grantId, activityIds },
    );
    return data;
  },

  /**
   * PATCH sync-config/ — Update sync direction flags.
   *
   * Setting `outboundExecutionMode: 'live'` requires
   * `confirmLiveOutbound: true` or the backend rejects with 400 —
   * the arming step is deliberately explicit.
   */
  async updateSyncConfig(
    updates: Partial<BookinglayerSyncConfig> & { confirmLiveOutbound?: boolean },
  ): Promise<BookinglayerSyncConfig> {
    const { data } = await apiClient.patch<BookinglayerSyncConfig>(
      `${BASE}/sync-config/`,
      updates,
      { preserveKeys: CONFIG_MAP_KEYS },
    );
    return data;
  },

  /**
   * POST pause/ — Pause integration (keeps config/keys, stops sync).
   * Backend: POST /api/integrations/bookinglayer/pause/
   */
  async disconnect(): Promise<void> {
    await apiClient.post(`${BASE}/pause/`);
  },

  /**
   * DELETE delete/ — Permanently delete integration connection.
   * Removes: BL webhooks, API key, sync config, all job history.
   * Backend: DELETE /api/integrations/bookinglayer/delete/
   */
  async deleteConnection(): Promise<void> {
    await apiClient.delete(`${BASE}/delete/`);
  },

  /** GET alerts/ — Recent health alerts.
   *  The endpoint responds with `{ alerts: [...], alertLevel }`, NOT a bare
   *  array (the level is also on the connection status). Return just the
   *  array — returning the whole object made AlertsDrawer's `alerts.filter`
   *  throw "d.filter is not a function" and crashed the status page. */
  async getAlerts(): Promise<BookinglayerAlert[]> {
    const { data } = await apiClient.get<{ alerts: BookinglayerAlert[] }>(`${BASE}/alerts/`, {
      // `context` is free-form diagnostic data — show it as stored.
      preserveKeys: ['context'],
    });
    return Array.isArray(data?.alerts) ? data.alerts : [];
  },

  /** POST recover/ — Clear error state + re-sync last 2 days */
  async recover(): Promise<{ jobId: string }> {
    const { data } = await apiClient.post<{ jobId: string }>(`${BASE}/recover/`);
    return { jobId: data.jobId };
  },

  /** POST register-webhook/ — Retry webhook registration, returns error detail on failure */
  async registerWebhook(): Promise<{ registered: boolean; blWebhookId?: number | null; error?: string; reused?: boolean }> {
    const { data } = await apiClient.post<{
      registered: boolean;
      blWebhookId?: number | null;
      error?: string;
      reused?: boolean;
    }>(`${BASE}/register-webhook/`);
    return data;
  },

  /**
   * GET outbound-events/ — Audit log of every Shaka → BL push.
   *
   * Paginated (limit + offset, hard cap 200 server-side) and filterable
   * by status (sent / failed / dry_run / blocked / skipped) and
   * resource_type. Pass arrays — they're sent as comma-separated lists.
   *
   * `payload` and `responseBody` are what actually went to / came back
   * from BL — preserved verbatim (no key camelization) so the forensic
   * trail matches BL's API docs.
   */
  async getOutboundEvents(params: {
    limit?: number;
    offset?: number;
    status?: OutboundEventStatus[];
    resourceType?: OutboundEventResourceType[];
  } = {}): Promise<OutboundEventsResponse> {
    const query = new URLSearchParams();
    if (params.limit != null) query.set('limit', String(params.limit));
    if (params.offset != null) query.set('offset', String(params.offset));
    if (params.status?.length) query.set('status', params.status.join(','));
    if (params.resourceType?.length) {
      query.set('resource_type', params.resourceType.join(','));
    }
    const suffix = query.toString() ? `?${query.toString()}` : '';

    type RawEvent = Omit<OutboundEventsResponse['events'][number], 'connectionId'> & {
      connection: string;
    };
    const { data } = await apiClient.get<{
      count: number;
      limit: number;
      offset: number;
      events: RawEvent[];
    }>(`${BASE}/outbound-events/${suffix}`, {
      preserveKeys: ['payload', 'responseBody'],
    });

    return {
      count: data.count,
      limit: data.limit,
      offset: data.offset,
      events: data.events.map(({ connection, ...rest }) => ({
        ...rest,
        connectionId: connection,
      })),
    };
  },

  /**
   * POST outbound-events/{id}/retry/ — Re-enqueue the original push task.
   *
   * The retry uses the source object's CURRENT state (not the snapshot
   * stored on the failed event), so editing the underlying guest /
   * booking and then retrying pushes the corrected payload. A new
   * BookinglayerOutboundEvent row is created — audit history stays
   * append-only.
   */
  async retryOutboundEvent(eventId: string): Promise<OutboundEventRetryResponse> {
    const { data } = await apiClient.post<OutboundEventRetryResponse>(
      `${BASE}/outbound-events/${eventId}/retry/`,
    );
    return data;
  },

  /**
   * GET webhook-logs/ — Inbound webhook requests received by Shaka.
   * Use `includeBody=true` to get headers + raw body + payload for debugging.
   * `headers` and `payload` are preserved verbatim — this feed exists to
   * show exactly what BL sent.
   */
  async getWebhookLogs(params: {
    limit?: number;
    outcome?: string;
    includeBody?: boolean;
  } = {}): Promise<WebhookLogsResponse> {
    const query = new URLSearchParams();
    if (params.limit != null) query.set('limit', String(params.limit));
    if (params.outcome) query.set('outcome', params.outcome);
    if (params.includeBody) query.set('include_body', 'true');
    const suffix = query.toString() ? `?${query.toString()}` : '';
    const { data } = await apiClient.get<WebhookLogsResponse>(
      `${BASE}/webhook-logs/${suffix}`,
      { preserveKeys: ['headers', 'payload'] },
    );
    return data;
  },

  /**
   * GET webhook-logs/{id}/ — one log WITH headers + raw body + payload.
   * Bodies carry guest PII, so the detail is the ONLY body fetch: the list
   * stays summaries-only and the drawer asks for exactly the row the
   * operator opened.
   */
  async getWebhookLog(logId: string): Promise<WebhookLogDetailResponse> {
    const { data } = await apiClient.get<WebhookLogDetailResponse>(
      `${BASE}/webhook-logs/${logId}/`,
      { preserveKeys: ['headers', 'payload'] },
    );
    return data;
  },
};
